import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from 'helpers/theme';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CoreInput from 'sharedComponents/core/Input/CoreInput';
import UserResults from './UserResults';
import { useAnyModalOpen } from 'views/ModalRegistryProvider';
import { useCloseActionMenu } from 'components/ActionMenu/ActionMenuProvider';
import { useRouter } from 'found';

const useStyles = createUseStyles((theme: Theme) => ({
  trigger: {
    display: 'flex',
    alignItems: 'center',
    height: 38,
    padding: '0 12px',
    background: theme.colors.backgroundGrey,
    border: '1px solid #ededed',
    borderRadius: theme.primaryBorderRadius,
    cursor: 'pointer',
    transition: 'background-color 0.15s, border-color 0.15s',
    '&:hover': {
      backgroundColor: '#f0f1f3'
    }
  },
  triggerIcon: {
    color: '#74777f',
    fontSize: 13
  },
  triggerText: {
    flex: 1,
    marginLeft: 10,
    color: '#74777f',
    fontSize: 13,
    fontWeight: 500,
    userSelect: 'none'
  },
  shortcutHint: {
    padding: '2px 7px',
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 6,
    color: theme.colors.secondaryGrey,
    fontSize: 11,
    fontWeight: 500,
    userSelect: 'none'
  },
  '@keyframes overlayIn': {
    from: { opacity: 0 },
    to: { opacity: 1 }
  },
  '@keyframes panelIn': {
    from: { opacity: 0, transform: 'scale(0.98) translateY(-10px)' },
    to: { opacity: 1, transform: 'scale(1) translateY(0)' }
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: '10vh',
    background: 'rgba(23, 25, 35, 0.35)',
    backdropFilter: 'blur(6px)',
    '-webkit-backdrop-filter': 'blur(6px)',
    animation: '$overlayIn 0.15s ease-out'
  },
  panel: {
    width: 760,
    maxWidth: 'calc(100vw - 40px)',
    background: 'white',
    borderRadius: 14,
    border: '1px solid #ededed',
    boxShadow: '0 16px 70px rgba(0, 0, 0, 0.2), 0 3px 12px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    animation: '$panelIn 0.15s ease-out'
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '18px 20px'
  },
  searchIcon: {
    color: '#74777f',
    fontSize: 16
  },
  searchInput: {
    fontSize: 17,
    fontWeight: 400,
    marginLeft: 13,
    color: theme.colors.primaryBlack,
    '&::placeholder': {
      color: theme.colors.secondaryGrey
    }
  },
  escHint: {
    extend: 'shortcutHint',
    background: theme.colors.backgroundGrey
  }
}));

export type SearchResult = {
  uuid: string;
  title: string;
  subtitle?: string;
  tag?: string;
  tagLabel?: string; // label inside the right-hand tag pill, defaults to TTC-ID
  action?: () => void; // run instead of navigating (takes priority over href/link)
  link?: string; // navigate within the app
  href?: string; // open in a new tab (takes priority over link)
  avatar?: { name: string; url?: string }; // render a ProfileIcon instead of the section icon
  iconColor?: string; // override the section icon colour for this result
  titleBadge?: string; // small pill shown next to the title
};

export type SearchSection = {
  label: string;
  icon: IconDefinition;
  results: SearchResult[];
};

type Props = {
  companyName: string;
  searchFunction: (query: string) => Promise<SearchSection[]>;
  debounceTime?: number;
  onFocusChange?: (val: boolean) => void;
};

const isMac =
  typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);

function UniversalSearch({
  companyName,
  searchFunction,
  onFocusChange,
  debounceTime = 400
}: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });
  const { router } = useRouter();
  const anyModalOpen = useAnyModalOpen();
  const closeActionMenu = useCloseActionMenu();

  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [sections, setSections] = React.useState<SearchSection[]>([]);
  const [input, setInput] = React.useState('');
  const [highlighted, setHighlighted] = React.useState(0);

  const debouncer = React.useRef<number>();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const flatResults = sections.reduce<SearchResult[]>(
    (all, section) => all.concat(section.results),
    []
  );

  const runSearch = (text: string, immediate = false) => {
    setLoading(true);
    clearTimeout(debouncer.current);
    debouncer.current = setTimeout(
      async () => {
        try {
          const res = await searchFunction(text);
          setSections(res);
        } catch (err) {
          console.error('Search failed', err);
          setSections([]);
        } finally {
          setLoading(false);
          setHighlighted(0);
        }
      },
      immediate ? 0 : debounceTime
    ) as unknown as number;
  };

  const openSearch = () => {
    setOpen(true);
    setInput('');
    setSections([]);
    setHighlighted(0);
    onFocusChange && onFocusChange(true);
    runSearch('', true);
  };

  const closeSearch = () => {
    clearTimeout(debouncer.current);
    setOpen(false);
    onFocusChange && onFocusChange(false);
  };

  const onChange = (text: string) => {
    setInput(text);
    runSearch(text);
  };

  const onSelect = (result: SearchResult) => {
    closeSearch();
    if (result.action) {
      result.action();
    } else if (result.href) {
      window.open(result.href, '_blank');
    } else if (result.link) {
      router.push(result.link);
    }
  };

  // Global shortcut to open, escape to close
  React.useEffect(() => {
    const onKeyDown = (evt: KeyboardEvent) => {
      if ((evt.metaKey || evt.ctrlKey) && evt.key.toLowerCase() === 'k') {
        // A modal owns the screen while it's open, and every result either
        // routes away or opens another modal, so leave the shortcut inert
        // rather than layering the palette over it. This runs before the menu
        // is dismissed below so that a modal shown via ActionMenu's 'modal'
        // placement is protected too.
        if (anyModalOpen) return;
        evt.preventDefault();
        // An open menu holds no state but its click-catching backdrop sits
        // above the palette, so drop it rather than open underneath it.
        closeActionMenu();
        open ? closeSearch() : openSearch();
      } else if (evt.key === 'Escape' && open) {
        closeSearch();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  // Focus the input and lock body scroll while open
  React.useEffect(() => {
    if (!open) return;
    inputRef.current && inputRef.current.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const onInputKeyDown = (evt: React.KeyboardEvent) => {
    if (evt.key === 'ArrowDown') {
      evt.preventDefault();
      setHighlighted((prev) => Math.min(prev + 1, flatResults.length - 1));
    } else if (evt.key === 'ArrowUp') {
      evt.preventDefault();
      setHighlighted((prev) => Math.max(prev - 1, 0));
    } else if (evt.key === 'Enter' && flatResults[highlighted]) {
      onSelect(flatResults[highlighted]);
    }
  };

  return (
    <div>
      <div className={classes.trigger} onClick={openSearch}>
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className={classes.triggerIcon}
        />
        <div className={classes.triggerText}>Search</div>
        <div className={classes.shortcutHint}>{isMac ? '⌘K' : 'Ctrl K'}</div>
      </div>
      {open && (
        <div className={classes.overlay} onMouseDown={closeSearch}>
          <div
            className={classes.panel}
            onMouseDown={(evt) => evt.stopPropagation()}
          >
            <div className={classes.searchRow} onKeyDown={onInputKeyDown}>
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className={classes.searchIcon}
              />
              <CoreInput
                type="search"
                placeholder={`Search ${companyName}`}
                onChange={onChange}
                className={classes.searchInput}
                value={input}
                ref={inputRef}
              />
              <div className={classes.escHint}>esc</div>
            </div>
            <UserResults
              loading={loading}
              sections={sections}
              query={input}
              highlighted={highlighted}
              onHighlight={setHighlighted}
              onSelect={onSelect}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default UniversalSearch;
